import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import PublicLayout from "@/components/PublicLayout";
import { useAgent } from "@/lib/agentContext";
import { track } from "@/lib/track";
import { ShieldCheck, MessageCircle, BadgeCheck, ArrowRight, MapPin, Phone } from "lucide-react";

export default function About() {
  const agent = useAgent();
  if (agent) return <AgentAbout agent={agent} />;
  return <CompanyAbout />;
}

function AgentAbout({ agent }) {
  const waNum = (agent.phone || "+919999999999").replace(/[^0-9]/g, "");
  const waHref = `https://wa.me/${waNum}?text=${encodeURIComponent(`Hi ${agent.name}, I'd like to know more about working with you.`)}`;
  const onWa = () => track({ event_type: "wa_click", agent_slug: agent.slug, source: "microsite" });

  return (
    <>
      <section className="bg-sg-navy text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-cover bg-center opacity-15"
             style={{ backgroundImage: "url(https://images.pexels.com/photos/21835254/pexels-photo-21835254.jpeg)" }} />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8 py-24 grid grid-cols-1 md:grid-cols-[200px_1fr] gap-10 items-center">
          <img
            src={agent.picture || "https://images.pexels.com/photos/7580994/pexels-photo-7580994.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=320&w=320"}
            alt={agent.name}
            className="w-44 h-44 rounded-full object-cover border-2 border-sg-gold/60"
            data-testid="agent-about-headshot"
          />
          <div>
            <div className="overline text-sg-gold mb-5">ABOUT · {agent.micro_market?.toUpperCase()} SPECIALIST</div>
            <h1 className="font-display text-5xl lg:text-6xl leading-[1.05]">
              {agent.name}
            </h1>
            <p className="mt-6 text-white/75 max-w-2xl">
              {agent.bio || `Independent property consultant operating across ${agent.primary_locality} and the wider ${agent.micro_market} corridor.`}
            </p>
            <div className="mt-5 flex flex-wrap items-center gap-2">
              <span className="chip chip-gold inline-flex items-center gap-1" data-testid="agent-about-rera">
                <BadgeCheck className="w-3.5 h-3.5" /> MahaCRITI Reg No: {agent.maharera_number}
              </span>
              <span className="chip"><MapPin className="w-3 h-3 mr-1" /> Primary · {agent.primary_locality}</span>
            </div>
            <div className="mt-7 flex flex-wrap gap-3">
              <a href={waHref} target="_blank" rel="noreferrer" onClick={onWa} className="pill-solid inline-flex items-center gap-2">
                <MessageCircle className="w-4 h-4" /> WhatsApp {agent.name.split(" ")[0]}
              </a>
              {agent.phone && (
                <a href={`tel:${agent.phone}`} className="pill-outline inline-flex items-center gap-2 text-white border-white/40 hover:bg-white hover:text-sg-navy">
                  <Phone className="w-4 h-4" /> Call
                </a>
              )}
            </div>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-8 py-20">
        <div className="overline text-sg-gold-deep mb-3">WHY WORK WITH {agent.name.split(" ")[0].toUpperCase()}</div>
        <h2 className="font-display text-4xl lg:text-5xl text-sg-navy max-w-3xl">
          A single-corridor specialist, not a <span className="font-serif-italic text-sg-gold-deep">spray-and-pray broker.</span>
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-12">
          {[
            { tag: `${agent.primary_locality?.toUpperCase()} FIRST`, h: "Deep micro-market knowledge", c: `Lives and transacts in ${agent.primary_locality}. Knows which towers actually trade and which are paper inventory.` },
            { tag: "MAHACRITI-VERIFIED", h: "Registered with the regulator", c: `Reg No: ${agent.maharera_number}. Every listing links back to this badge — you can verify it on the MahaCRITI portal.` },
            { tag: "WHATSAPP-NATIVE", h: "Your thread, your pace", c: "No call centre. Brief on WhatsApp, get verified inventory back the same day, decide on your timeline." },
          ].map((p) => (
            <div key={p.tag} className="sg-card p-7">
              <div className="w-10 h-10 rounded-full bg-sg-cream-soft border border-sg-line flex items-center justify-center text-sg-gold-deep">
                <ShieldCheck className="w-4 h-4" />
              </div>
              <div className="overline text-sg-gold-deep mt-5">{p.tag}</div>
              <h3 className="font-display text-2xl mt-1 text-sg-navy">{p.h}</h3>
              <p className="mt-3 text-sg-mute text-sm">{p.c}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-8 pb-20">
        <div className="sg-card-cream flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div>
            <div className="overline text-sg-gold-deep mb-2">READY TO BRIEF?</div>
            <h3 className="font-display text-3xl text-sg-navy max-w-xl">
              Tell {agent.name.split(" ")[0]} what you're looking for.
            </h3>
            <p className="text-sm text-sg-mute mt-3 max-w-xl">
              One WhatsApp message — locality, budget, configuration, timeline. You'll get a curated reply within the hour during business hours.
            </p>
          </div>
          <div className="flex gap-2">
            <a href={waHref} target="_blank" rel="noreferrer" onClick={onWa} className="pill-solid inline-flex items-center gap-2">
              <MessageCircle className="w-4 h-4" /> Send a brief <ArrowRight className="w-4 h-4" />
            </a>
            <Link to={`/agent/${agent.slug}/buy`} className="pill-outline inline-flex items-center gap-2">Browse Inventory</Link>
          </div>
        </div>
      </section>
    </>
  );
}

function CompanyAbout() {
  const [stats, setStats] = useState({ projects_count: 135, localities_count: 9 });
  useEffect(() => {
    api.get("/public-stats").then(({ data }) => setStats(data)).catch(() => {});
  }, []);
  return (
    <PublicLayout>
      {/* HERO */}
      <section className="bg-sg-navy text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-cover bg-center opacity-20"
             style={{ backgroundImage: "url(https://images.pexels.com/photos/21835254/pexels-photo-21835254.jpeg)" }} />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8 py-24 lg:py-32">
          <div className="overline text-sg-gold mb-5">ABOUT INPROPPORTAL</div>
          <h1 className="font-display text-5xl lg:text-7xl leading-[0.95] max-w-4xl">
            We're not another listings site. We're <span className="font-serif-italic text-sg-gold">the agent's portal.</span>
          </h1>
          <p className="mt-8 text-lg text-white/75 max-w-2xl leading-relaxed">
            INPropPortal exists to give Pune's independent agents and brokerages the same toolkit that the biggest brokerages keep behind closed doors — WhatsApp-native ingestion, MahaCRITI-verified branded portals, and per-tap lead receipts. Built in Pune, for Pune.
          </p>
        </div>
      </section>

      {/* WHAT WE BELIEVE */}
      <section className="max-w-7xl mx-auto px-6 lg:px-8 py-20">
        <div className="overline text-sg-gold-deep mb-3">WHAT WE BELIEVE</div>
        <h2 className="font-display text-4xl lg:text-5xl text-sg-navy max-w-3xl">
          Three convictions that shape every line of code.
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-12">
          {[
            {
              icon: <ShieldCheck className="w-5 h-5" />,
              tag: "ZERO-TRUST",
              title: "PII never reaches the wire.",
              body: "Every WhatsApp forward is run through edge redaction. Names, phones, Aadhar, PAN, unit numbers — dropped at the LLM step, never stored.",
            },
            {
              icon: <BadgeCheck className="w-5 h-5" />,
              tag: "COMPLIANCE-FIRST",
              title: "MahaCRITI on every listing.",
              body: "An agent's RERA number isn't a footer afterthought — it's on the header, every project card, and stamped into every WhatsApp deep-link.",
            },
            {
              icon: <MessageCircle className="w-5 h-5" />,
              tag: "WHATSAPP-NATIVE",
              title: "Built for how brokers actually work.",
              body: "Manual web forms are disabled. Brokers paste blasts, agents publish, buyers tap WhatsApp. The medium is the message.",
            },
          ].map((p) => (
            <div key={p.tag} className="sg-card p-7">
              <div className="w-10 h-10 rounded-full bg-sg-cream-soft border border-sg-line flex items-center justify-center text-sg-gold-deep">{p.icon}</div>
              <div className="overline text-sg-gold-deep mt-5">{p.tag}</div>
              <h3 className="font-display text-2xl mt-1 text-sg-navy">{p.title}</h3>
              <p className="mt-3 text-sg-mute text-sm leading-relaxed">{p.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* BY THE NUMBERS */}
      <section className="bg-white border-y border-sg-line py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="overline text-sg-gold-deep mb-3">BY THE NUMBERS</div>
          <h2 className="font-display text-4xl lg:text-5xl text-sg-navy max-w-3xl">
            Hyper-local from <span className="font-serif-italic text-sg-gold-deep">day one.</span>
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-12">
            {[
              [String(stats.projects_count), "MASTER PROJECTS", "about-stat-projects"],
              [String(stats.localities_count), "PUNE LOCALITIES", "about-stat-localities"],
              ["2", "CORRIDORS · E/W", "about-stat-corridors"],
              ["100%", "MAHACRITI-ALIGNED", "about-stat-compliance"],
            ].map(([n, label, tid]) => (
              <div key={label} className="sg-card-cream text-center" data-testid={tid}>
                <div className="rupee font-display text-5xl text-sg-navy">{n}</div>
                <div className="overline text-sg-mute mt-2">{label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SERVING */}
      <section className="max-w-7xl mx-auto px-6 lg:px-8 py-20 grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="sg-card-dark">
          <div className="overline text-sg-gold mb-2">FOR AGENTS</div>
          <h3 className="font-display text-3xl leading-tight">
            One MahaCRITI badge. One branded portal. <span className="font-serif-italic text-sg-gold">Every lead, receipted.</span>
          </h3>
          <p className="mt-4 text-white/75 text-sm leading-relaxed">
            14-day setup compressed to 14 minutes. Sign in with Google, declare your corridor, paste your WhatsApp inventory, ship.
          </p>
          <Link to="/login" className="pill-solid inline-flex items-center gap-2 mt-6" data-testid="about-cta-agent">
            Claim Your Portal <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
        <div className="sg-card-cream">
          <div className="overline text-sg-gold-deep mb-2">FOR BUYERS · TENANTS</div>
          <h3 className="font-display text-3xl text-sg-navy leading-tight">
            Browse Pune's best — without the cold calls.
          </h3>
          <p className="mt-4 text-sg-mute text-sm leading-relaxed">
            Every project is MahaCRITI-verified. Every WhatsApp tap goes to a real, accountable agent. No spam pools.
          </p>
          <div className="flex gap-2 mt-6">
            <Link to="/buy" className="pill-solid inline-flex items-center gap-2">Browse Homes <ArrowRight className="w-4 h-4" /></Link>
            <Link to="/market-insights" className="pill-outline inline-flex items-center gap-2">Market Insights</Link>
          </div>
        </div>
      </section>

      {/* TEAM / VALUES STRIP */}
      <section className="max-w-7xl mx-auto px-6 lg:px-8 pb-24">
        <div className="bg-white border border-sg-line rounded-3xl p-10 flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div>
            <div className="overline text-sg-gold-deep mb-2">BUILT IN PUNE</div>
            <h3 className="font-display text-3xl text-sg-navy max-w-xl">
              By people who've waited on a developer's site office.
            </h3>
            <p className="text-sm text-sg-mute mt-3 max-w-xl">
              No marketing fluff. If something is mocked, it says mocked. If a number is real, it's a live aggregate.
            </p>
          </div>
          <div className="flex gap-3">
            <a href="mailto:hello@inpropportal.com" className="pill-outline inline-flex items-center gap-2"><MessageCircle className="w-4 h-4" /> Talk to us</a>
          </div>
        </div>
      </section>
      {/* RECOGNITION · TOI AI QUOTIENT AWARDS NOMINEE */}
      <section className="max-w-7xl mx-auto px-6 lg:px-8 pb-24" data-testid="about-toi-aiq-badge">
        <div className="bg-white border border-sg-line rounded-3xl p-10 flex flex-col items-center text-center gap-3">
          <div className="relative overflow-hidden w-full">
            <img
              src="https://customer-assets-7cd3h4nn.emergentagent.net/job_pune-property-ai/artifacts/7yj6lltp_TIO%20AIQ%202026.png"
              alt="TOI AI Quotient Awards 2026 — Nominee"
              className="w-[128px] h-auto animate-marquee-single"
              loading="lazy"
              decoding="async"
            />
          </div>
          <p className="text-sm text-sg-mute">Nominee · TOI AI Quotient Awards 2026</p>
        </div>
      </section>
    </PublicLayout>
  );
}
